import * as THREE from 'three';
import { chunkKey, CHUNK_SIZE, LOD_RES, type ChunkData } from './chunk';
import type { Region } from './region';
import type { ChunkRequest } from './chunkWorker';
import { ChunkView } from '@/render/chunkView';
import type { VegetationKit } from '@/render/vegetation';
import type { PlantPhenotypeSource } from '@/render/phenotype';

interface Job { cx: number; cz: number; lod: number; d: number; key: string }

/**
 * The coarsest rung `chunk.ts` still grows plants on. A chunk at or under this
 * LOD carries its own trees; past it the far canopy has to.
 */
const VEG_LOD = 2;
/**
 * How far LOD 2 — the coarsest rung that still grows trees — is allowed to reach,
 * in chunks. Three kilometres of streamed forest is about where a tree stops
 * being worth an instanced draw call: past it the far canopy covers the same
 * ground for two calls total, so the rim is bare LOD-3 ground that it plants.
 */
const VEG_RING = 12;

/**
 * Streams chunks around a focus point.
 *
 * The set of chunks that should exist is a pure function of the focus position
 * and the view distance — never of how you got there. Building runs on a small
 * pool of Web Workers, nearest first, capped in flight.
 *
 * Two rules keep movement smooth rather than glitchy:
 *
 * - **Nothing is thrown away eagerly.** A view that leaves the ring is
 *   unmounted into an LRU cache, not disposed. Coming back remounts it
 *   instantly — no rebuild, no pop. Disposal happens only when the cache
 *   overflows, oldest first.
 *
 * - **Nothing is unmounted before its replacement is ready.** When a cell's
 *   LOD changes, the old view keeps rendering until the new LOD's view is
 *   actually live. No holes while the worker catches up.
 */
export class ChunkManager {
  /** Mounted views, in the scene. */
  private readonly live = new Map<string, ChunkView>();
  /** Built but unmounted views. Map iteration order doubles as LRU order. */
  private readonly cache = new Map<string, ChunkView>();
  /**
   * Views whose ground truth changed under them — invalidated, and still in
   * the scene as their own stand-in until the worker's replacement mounts.
   * The second rule above, applied to `invalidate()`, which used to remove
   * and dispose the view on the spot: measured 2026-08-15, a chunk rebuilds
   * 54–67 ms after invalidation, so a cast's FIRST visible answer was the
   * ground under it vanishing for ~4 frames at 60 fps and the far field
   * showing through. Not in `live` (so `update` re-requests the key) and not
   * in `cache` (so nothing remounts it); nothing but the scene graph reads it.
   */
  private readonly stale = new Map<string, ChunkView>();
  /** Keys currently out with a worker. */
  private readonly inFlight = new Set<string>();
  /** Keys that should exist as of the last update(). */
  private wanted = new Set<string>();
  /** The wanted LOD key for each wanted cell, by "cx,cz". */
  private wantedCell = new Map<string, string>();
  /** Wanted-but-unbuilt jobs, nearest first, refreshed every frame. */
  private queue: Job[] = [];
  readonly root = new THREE.Group();

  private readonly workers: Worker[] = [];
  private readonly idle: Worker[] = [];
  /** What each busy worker is building, so a crash can free its slot. */
  private readonly busyKey = new Map<Worker, string>();
  private readonly maxInFlight: number;
  private readonly cacheCap: number;

  /**
   * Live multiplier on the LOD rungs' distances — the settings menu's "lod
   * detail" knob. 1 is the shipped tuning; 2 pushes every rung twice as far
   * out (LOD 0 to six chunks), 0.5 pulls them all inward. Render-only, read
   * on every update, so a change re-evaluates the wanted set on the next
   * frame and the streamer rebuilds exactly the chunks whose rung changed.
   */
  detailScale = 1;

  /**
   * Applied to every freshly built chunk before it becomes a view. This is
   * where mutable world state (burn scars, future diffs) stamps itself onto
   * the pure baseline — the workers never know it exists. Invalidate a cell
   * and the next build passes through here again with current state.
   */
  private readonly transform: (data: ChunkData) => ChunkData;
  /** The plant phenotype source every built view reads, or null. */
  private readonly phenotype: PlantPhenotypeSource | null;

  constructor(
    region: Region,
    /** Geometry per plant kind and the shared material — owned by the caller. */
    private readonly veg: VegetationKit,
    opts: {
      workers?: number; maxInFlight?: number; cacheCap?: number;
      transform?: (data: ChunkData) => ChunkData;
      /**
       * The plant phenotype source, handed to every ChunkView this manager
       * builds (`render/phenotype.ts`). Absent (the shipped world) costs
       * nothing anywhere. Like `transform`, it is part of what a built view
       * means — a caller that changes what a source answers invalidates the
       * cells it changed, and the next build reads the new answer.
       */
      phenotype?: PlantPhenotypeSource | null;
    } = {},
  ) {
    this.transform = opts.transform ?? ((d) => d);
    this.phenotype = opts.phenotype ?? null;
    // A Region's methods do not survive structured clone, but its data does:
    // send the built arrays and let each worker wrap a Region around them,
    // rather than paying the multi-second build once per worker.
    const snapshot = region.toSnapshot();
    const poolSize = Math.max(1, opts.workers ?? Math.min(4, (navigator.hardwareConcurrency ?? 4) - 1));
    this.maxInFlight = Math.max(1, opts.maxInFlight ?? poolSize);
    // must comfortably exceed one full ring (2401), or a long move floods the
    // cache and evicts home before the player turns around
    this.cacheCap = Math.max(0, opts.cacheCap ?? 5000);

    for (let i = 0; i < poolSize; i++) {
      const w = new Worker(new URL('./chunkWorker.ts', import.meta.url), { type: 'module' });
      w.postMessage({ type: 'init', snapshot } satisfies ChunkRequest);
      w.onmessage = (e: MessageEvent<ChunkData>) => this.onResult(w, e.data);
      w.onerror = (e) => this.onError(w, e);
      this.workers.push(w);
      this.idle.push(w);
    }
  }

  /**
   * LOD by distance in chunks: fine underfoot, coarse at the rim.
   *
   * The rungs are as wide as the frame can pay for, and what they cost is not
   * the vertex ladder (65/33/17/9 — LOD 2 is 640 triangles) but draw calls and
   * plant instances. That cost is very unevenly distributed: LOD 0 keeps the
   * grass and LOD 1 keeps the mounds, so each of those chunks is three or four
   * instanced draws and thousands of instances, while an LOD 2 chunk is a single
   * draw of a few dozen trees. So LOD 0 and 1 widen by one ring each —
   * measurably, that is all they are worth — and LOD 2, the cheap rung that
   * still grows trees, carries the rest out to `VEG_RING`.
   *
   * Past that the rim is bare LOD-3 ground, which used to mean bald ground.
   * `render/farCanopy.ts` plants it now, and the whole island beyond it, for two
   * draw calls — so the streamer no longer has to reach for the horizon.
   */
  private lodFor(dist: number, ring: number): number {
    const s = this.detailScale;
    if (dist <= 3 * s) return 0;
    if (dist <= 6 * s) return 1;
    return dist <= Math.max(6 * s, Math.min(VEG_RING * s, ring - 1)) ? 2 : 3;
  }

  /**
   * `eyeX/eyeZ` is where the camera actually is, when the caller knows it.
   *
   * The ring stays centred on the focus — that is what keeps orbiting cheap,
   * because swinging around a target streams nothing new — but detail is spent
   * against the *eye*, because that is where the pixels are. At a shallow
   * elevation the ground directly beneath the camera can sit eight chunks from
   * the focus, and giving it LOD 2 while the distant target gets LOD 0 is
   * exactly backwards: the coarse mesh is the one filling the bottom of the
   * frame. Omit the eye and it falls back to the focus, as before.
   */
  update(focusX: number, focusZ: number, viewRadius: number,
         eyeX = focusX, eyeZ = focusZ) {
    const cx0 = Math.floor(focusX / CHUNK_SIZE);
    const cz0 = Math.floor(focusZ / CHUNK_SIZE);
    const ex = Math.floor(eyeX / CHUNK_SIZE), ez = Math.floor(eyeZ / CHUNK_SIZE);
    const ring = Math.min(24, Math.max(2, Math.ceil(viewRadius / CHUNK_SIZE)));

    this.wanted.clear();
    this.wantedCell.clear();
    const desired: Job[] = [];
    for (let dz = -ring; dz <= ring; dz++) for (let dx = -ring; dx <= ring; dx++) {
      const d = Math.max(Math.abs(dx), Math.abs(dz));
      if (d > ring) continue;
      const cx = cx0 + dx, cz = cz0 + dz;
      // Detail by EUCLIDEAN distance from the eye; membership and build order
      // stay on the square ring. Chebyshev detail made every LOD transition a
      // square drawn on the land — a corner of grass ending along two straight
      // lines is the streamer showing through, where a circle of it is just
      // distance doing what distance does.
      const dE = Math.hypot(dx, dz);
      const eyeD = Math.hypot(cx - ex, cz - ez);
      const lod = Math.min(this.lodFor(Math.min(dE, eyeD), ring), LOD_RES.length - 1);
      const key = chunkKey(cx, cz, lod);
      this.wanted.add(key);
      this.wantedCell.set(`${cx},${cz}`, key);
      if (this.live.has(key)) continue;
      if (this.cache.has(key)) { this.mountFromCache(key); continue; }
      if (!this.inFlight.has(key)) desired.push({ cx, cz, lod, d, key });
    }

    // Rebuilt fresh each frame: jobs that stopped being wanted simply drop out.
    desired.sort((a, b) => a.d - b.d);
    this.queue = desired;

    // Unmount pass. A live view survives if it is the wanted LOD for its cell,
    // or if it is standing in for a wanted LOD that is not yet ready.
    for (const [key, view] of this.live) {
      if (this.wanted.has(key)) continue;
      const [cx, cz] = key.split(',');
      const wantedKey = this.wantedCell.get(`${cx},${cz}`);
      if (wantedKey !== undefined && !this.live.has(wantedKey)) continue;  // hold the fort
      this.unmountToCache(key, view);
    }
    // A stale stand-in goes once its cell has what it wants, or wants nothing
    // here any more. (`onResult` also drops it the moment the replacement
    // mounts; this catches the cell the player flew away from meanwhile.)
    for (const [key, view] of this.stale) {
      const [cx, cz] = key.split(',');
      const wantedKey = this.wantedCell.get(`${cx},${cz}`);
      if (wantedKey === undefined || this.live.has(wantedKey)) this.dropStale(key, view);
    }

    this.pump();
  }

  private dropStale(key: string, view: ChunkView) {
    this.root.remove(view.group);
    this.stale.delete(key);
    view.dispose();
  }

  private mountFromCache(key: string) {
    const view = this.cache.get(key)!;
    this.cache.delete(key);
    this.live.set(key, view);
    this.root.add(view.group);
  }

  private unmountToCache(key: string, view: ChunkView) {
    this.root.remove(view.group);
    this.live.delete(key);
    this.cache.delete(key);          // refresh recency if somehow present
    this.cache.set(key, view);
    this.trimCache();
  }

  private trimCache() {
    while (this.cache.size > this.cacheCap) {
      const [key, view] = this.cache.entries().next().value as [string, ChunkView];
      this.cache.delete(key);
      view.dispose();
    }
  }

  /** Fill idle workers from the queue, up to the in-flight cap. */
  private pump() {
    while (this.idle.length && this.queue.length && this.inFlight.size < this.maxInFlight) {
      const job = this.queue.shift()!;
      if (this.live.has(job.key) || this.cache.has(job.key) || this.inFlight.has(job.key)) continue;
      const w = this.idle.pop()!;
      this.inFlight.add(job.key);
      this.busyKey.set(w, job.key);
      w.postMessage({ type: 'build', cx: job.cx, cz: job.cz, lod: job.lod } satisfies ChunkRequest);
    }
  }

  private onResult(w: Worker, data: ChunkData) {
    const key = chunkKey(data.cx, data.cz, data.lod);
    this.inFlight.delete(key);
    this.busyKey.delete(w);
    this.idle.push(w);

    if (!this.live.has(key)) {
      const view = new ChunkView(this.transform(data), this.veg, this.phenotype);
      if (this.wanted.has(key)) {
        this.live.set(key, view);
        this.root.add(view.group);
        // the new LOD is ready — release any stand-in at another LOD
        const [cx, cz] = key.split(',');
        for (let lod = 0; lod < LOD_RES.length; lod++) {
          const other = chunkKey(Number(cx), Number(cz), lod);
          if (other !== key && this.live.has(other)) this.unmountToCache(other, this.live.get(other)!);
          // …and the stale stand-in this build replaces, at any LOD
          const st = this.stale.get(other);
          if (st) this.dropStale(other, st);
        }
      } else {
        // built for a view that moved on — keep it warm rather than waste it
        this.cache.set(key, view);
        this.trimCache();
      }
    }
    this.pump();
  }

  private onError(w: Worker, e: ErrorEvent) {
    const key = this.busyKey.get(w);
    if (key === undefined) return;   // not mid-job; nothing to recover
    this.inFlight.delete(key);
    this.busyKey.delete(w);
    this.idle.push(w);
    console.error(`chunk worker failed on ${key}: ${e.message}`);
    this.pump();
  }

  /**
   * Which cells around the focus have a live view at some LOD, as a bitmap
   * for the horizon's per-cell hole: 255 where a chunk is actually mounted
   * (discard the coarse mesh — real relief is rendering), 0 where nothing
   * is (keep it — better coarse ground than the void). `out` must be
   * (2·halfCells+1)² bytes, row-major, minimum z first.
   *
   * `outVeg`, if given, gets the same bitmap narrowed to the chunks that grow
   * their own plants (`VEG_LOD` and finer) — what the far canopy needs, because
   * a bare LOD-3 chunk is ground it should be planting, not ground it should be
   * yielding to. Both come out of one walk: parsing two thousand keys twice a
   * frame is not free.
   */
  occupancyInto(
    out: Uint8Array, focusX: number, focusZ: number, halfCells: number,
    outVeg?: Uint8Array,
  ): void {
    out.fill(0);
    outVeg?.fill(0);
    const cx0 = Math.floor(focusX / CHUNK_SIZE);
    const cz0 = Math.floor(focusZ / CHUNK_SIZE);
    const n = halfCells * 2 + 1;
    const mark = (key: string) => {
      const a = key.indexOf(','), b = key.lastIndexOf(',');
      const dx = Number(key.slice(0, a)) - cx0 + halfCells;
      const dz = Number(key.slice(a + 1, b)) - cz0 + halfCells;
      if (dx < 0 || dx >= n || dz < 0 || dz >= n) return;
      out[dz * n + dx] = 255;
      if (outVeg !== undefined && Number(key.slice(b + 1)) <= VEG_LOD) outVeg[dz * n + dx] = 255;
    };
    for (const key of this.live.keys()) mark(key);
    // a stale stand-in is still drawn, so the far layers must still make way
    for (const key of this.stale.keys()) mark(key);
  }

  /**
   * Forget everything built for a cell, at every LOD. The hook for future
   * chunk updates: when the diff store changes ground truth here, call this
   * and the next update() rebuilds the cell from the workers.
   */
  invalidate(cx: number, cz: number) {
    for (let lod = 0; lod < LOD_RES.length; lod++) {
      const key = chunkKey(cx, cz, lod);
      const mounted = this.live.get(key);
      if (mounted) {
        // stays in the scene, drawn from the OLD truth, until the replacement
        // is live — see `stale`. A second invalidation before the first
        // rebuild lands keeps the newer of the two stand-ins.
        this.live.delete(key);
        const prev = this.stale.get(key);
        if (prev && prev !== mounted) this.dropStale(key, prev);
        this.stale.set(key, mounted);
      }
      const cached = this.cache.get(key);
      if (cached) { this.cache.delete(key); cached.dispose(); }
    }
  }

  dispose() {
    for (const w of this.workers) w.terminate();
    this.workers.length = 0;
    this.idle.length = 0;
    this.busyKey.clear();
    this.inFlight.clear();
    for (const view of this.live.values()) { this.root.remove(view.group); view.dispose(); }
    this.live.clear();
    for (const view of this.cache.values()) view.dispose();
    this.cache.clear();
    for (const view of this.stale.values()) { this.root.remove(view.group); view.dispose(); }
    this.stale.clear();
  }

  get stats() {
    return {
      live: this.live.size,
      cached: this.cache.size,
      queued: this.queue.length + this.inFlight.size,
    };
  }
}
